import React, { useState, useEffect } from "react";
import moment from "moment";
import { useAppContext } from "../../AppContext";
import MPHomeLoginWithPhoneNumber from "./MPHomeLoginWithPhoneNumber";
import { useTranslation } from "react-i18next";

export default function MPHomeBookApp() {
  const {
    OpenDrawer,
    setOpenDrawer,
    ObjectData,
    setObjectData,
    AllApps,
    Loading,
    ChoosedDate,
    setChoosedDate,
    ChoosedApps,
    setChoosedApps,
    AllData,
    SelectedServices,
    setSelectedServices,
    User,
    setUser,
    OtherBarbers,
    setStep,
    step,
  } = useAppContext();
  const { t, i18n } = useTranslation();

  const [SelectedBarber, setSelectedBarber] = useState(null);

  // Check if we should show the barber selection step
  const shouldShowBarberStep = OtherBarbers && OtherBarbers.length > 1; // Changed from > 0 to > 1

  // Initialize user from localStorage on mount
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        if (parsedUser && !User) {
          setUser(parsedUser);
          // Auto-advance to appropriate step if already logged in
          if (shouldShowBarberStep) {
            setStep(4); // Go to barber selection
          } else {
            setStep(5); // Skip barber selection, go to services
          }
        }
      } catch (err) {
        console.error("Error parsing stored user:", err);
        localStorage.removeItem("user");
      }
    }
  }, []);

  // Add this helper function at the top of your component
  const getCurrencySymbol = (countryCode) => {
    const currencyMap = {
      "+972": "₪", // Israel - Shekel
      "+46": "kr", // Sweden - Krona
      "+1": "$", // US/Canada - Dollar
      "+44": "£", // UK - Pound
      "+49": "€", // Germany - Euro
    };

    return currencyMap[countryCode] || "₪"; // Default to shekel
  };

  const currencySymbol = getCurrencySymbol(User?.countryNumber);

  // Reset to login step when user logs out
  useEffect(() => {
    if (!User) {
      setStep(1);
      setSelectedServices([]);
      setChoosedDate(null);
      setChoosedApps([]);
      setSelectedBarber(null);
    } else {
      // If user exists and we're on step 1-3, move to appropriate step
      if (step >= 1 && step <= 3) {
        if (shouldShowBarberStep) {
          setStep(4); // Go to barber selection
        } else {
          setStep(5); // Skip to services
        }
      }
    }
  }, [User]);

  const toggleService = (item) => {
    setSelectedServices((prev) => {
      const exists = prev.some((s) => s.Service === item.service);
      if (exists) {
        return prev.filter((s) => s.Service !== item.service);
      } else {
        // Parse duration - handle formats like "30 min", "45 minutes", or just numbers
        let durationValue = 30; // default
        if (item.duration) {
          const parsed = parseInt(
            item.duration.toString().match(/\d+/)?.[0] || "30",
          );
          durationValue = parsed || 30;
        }

        return [
          ...prev,
          {
            Service: item.service,
            Price: item.price,
            Duration: durationValue,
          },
        ];
      }
    });
  };

  const days = Array.from({ length: 30 }, (_, i) => {
    const dayMoment = moment().add(i, "days");
    const dayIndex = dayMoment.day();
    const monthIndex = dayMoment.month();

    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    return {
      date: dayMoment.format("DD-MM-YYYY"),
      day: t(dayNames[dayIndex]),
      fullDay: t(dayNames[dayIndex]),
      dayNum: dayMoment.format("DD"),
      monthName: t(monthNames[monthIndex]),
    };
  });

  const totalPrice = SelectedServices.reduce(
    (sum, service) => sum + parseFloat(service.Price || 0),
    0,
  );

  const totalDuration = SelectedServices.reduce(
    (sum, service) => sum + (service.Duration || 0),
    0,
  );

  const getAvailableStartingSlots = (appointments, requiredDuration) => {
    if (requiredDuration === 0) {
      return appointments
        .map((app, index) => ({ app, index }))
        .filter(({ app }) => app.isTaken === 0)
        .map(({ index }) => index);
    }

    const validStartIndexes = [];
    const consumedIndexes = new Set();

    for (let i = 0; i < appointments.length; i++) {
      const app = appointments[i];

      if (consumedIndexes.has(i) || app.isTaken === 1 || app.isTaken === 4) {
        continue;
      }

      let accumulatedTime = 0;
      let currentIndex = i;
      const slotsNeeded = [];

      while (
        currentIndex < appointments.length &&
        accumulatedTime < requiredDuration
      ) {
        const currentApp = appointments[currentIndex];

        if (currentApp.isTaken === 1 || currentApp.isTaken === 4) {
          break;
        }

        if (currentIndex > i) {
          const prevApp = appointments[currentIndex - 1];
          if (prevApp.timeEnd !== currentApp.timeStart) {
            break;
          }
        }

        slotsNeeded.push(currentIndex);
        accumulatedTime += currentApp.timeLength;
        currentIndex++;
      }

      if (accumulatedTime >= requiredDuration) {
        validStartIndexes.push(i);
        for (let j = 1; j < slotsNeeded.length; j++) {
          consumedIndexes.add(slotsNeeded[j]);
        }
      }
    }

    return validStartIndexes;
  };

  const handleNextToServices = () => {
    if (SelectedBarber || !shouldShowBarberStep) {
      setStep(5);
    }
  };
  const handleNextToDateTime = () => {
    if (SelectedServices.length > 0) {
      const today = moment().format("DD-MM-YYYY");
      setChoosedDate(today);

      // Get appointments for the selected barber
      let todaysAppointments = [];

      if (SelectedBarber) {
        // Find the barber's data from allBarbersWithData
        const barberData = AllData.allBarbersWithData?.find(
          (b) => b.barber.phoneNumber === SelectedBarber.phoneNumber,
        );

        // Filter by date and isTaken === 0
        todaysAppointments =
          barberData?.appointments.filter(
            (app) => app.date === today && app.isTaken === 0,
          ) || [];
      } else {
        // If no barber selected, use the main barber's appointments
        todaysAppointments = AllApps.filter((app) => app.date === today);
      }

      setChoosedApps(todaysAppointments);
      setStep(6);
    }
  };
  const handleBackToBarber = () => {
    if (shouldShowBarberStep) {
      setStep(4);
    }
  };

  const handleBackToServices = () => {
    setStep(5);
  };

  const handleLoginSuccess = (userData) => {
    console.log("Login successful, saving user:", userData);

    if (userData) {
      setUser(userData);
      localStorage.setItem("user", JSON.stringify(userData));

      if (shouldShowBarberStep) {
        setStep(4);
      } else {
        setStep(5);
      }
    }
  };

  if (!Loading) return <div>{t("Loading")}...</div>;

  const getDisplayStep = (actualStep) => {
    if (!shouldShowBarberStep) {
      if (actualStep <= 3) return 1;
      if (actualStep === 5) return 2;
      if (actualStep === 6) return 3;
    } else {
      if (actualStep <= 3) return 1;
      if (actualStep === 4) return 2;
      if (actualStep === 5) return 3;
      if (actualStep === 6) return 4;
    }
    return 1;
  };

  const totalSteps = shouldShowBarberStep ? 4 : 3;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "24px",
        paddingBottom: "40px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          paddingTop: 20,
        }}
      >
        <a style={{ fontSize: 18 }}>Book appointment</a>
      </div>
      {/* Overall Progress Indicator - Only show after authentication */}
      {step > 3 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            //paddingTop: "20px",
          }}
        >
          {shouldShowBarberStep && (
            <>
              <div
                style={{ display: "flex", alignItems: "center", gap: "6px" }}
              >
                <div
                  style={{
                    width: "28px",
                    height: "28px",
                    borderRadius: "50%",
                    backgroundColor:
                      step === 4 ? "#667eea" : step > 4 ? "#10b981" : "#e5e7eb",
                    color: step >= 4 ? "#fff" : "#9ca3af",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: "600",
                    fontSize: "12px",
                  }}
                >
                  {step > 4 ? "✓" : "1"}
                </div>
                <span
                  style={{
                    fontSize: "13px",
                    fontWeight: step === 4 ? "600" : "500",
                    color:
                      step === 4 ? "#667eea" : step > 4 ? "#10b981" : "#9ca3af",
                  }}
                >
                  {t("Barber")}
                </span>
              </div>

              <div
                style={{
                  width: "30px",
                  height: "2px",
                  backgroundColor: step > 4 ? "#10b981" : "#e5e7eb",
                }}
              />
            </>
          )}

          {/* Services Step */}
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <div
              style={{
                width: "28px",
                height: "28px",
                borderRadius: "50%",
                backgroundColor:
                  step === 5 ? "#667eea" : step > 5 ? "#10b981" : "#e5e7eb",
                color: step >= 5 ? "#fff" : "#9ca3af",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: "600",
                fontSize: "12px",
              }}
            >
              {step > 5 ? "✓" : shouldShowBarberStep ? "2" : "1"}
            </div>
            <span
              style={{
                fontSize: "13px",
                fontWeight: step === 5 ? "600" : "500",
                color:
                  step === 5 ? "#667eea" : step > 5 ? "#10b981" : "#9ca3af",
              }}
            >
              {t("Services")}
            </span>
          </div>

          <div
            style={{
              width: "30px",
              height: "2px",
              backgroundColor: step > 5 ? "#667eea" : "#e5e7eb",
            }}
          />

          {/* Date & Time Step */}
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <div
              style={{
                width: "28px",
                height: "28px",
                borderRadius: "50%",
                backgroundColor: step === 6 ? "#667eea" : "#e5e7eb",
                color: step === 6 ? "#fff" : "#9ca3af",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: "600",
                fontSize: "12px",
              }}
            >
              {shouldShowBarberStep ? "3" : "2"}
            </div>
            <span
              style={{
                fontSize: "13px",
                fontWeight: step === 6 ? "600" : "500",
                color: step === 6 ? "#667eea" : "#9ca3af",
              }}
            >
              {t("Book")}
            </span>
          </div>
        </div>
      )}

      {/* STEPS 1-3: Authentication */}
      {step >= 1 && step <= 3 && (
        <div
          style={{
            maxWidth: "500px",
            margin: "0 auto",
            width: "100%",
          }}
        >
          <MPHomeLoginWithPhoneNumber onLoginSuccess={handleLoginSuccess} />
        </div>
      )}

      {/* STEP 4: Choose Barber */}
      {step === 4 && shouldShowBarberStep && (
        <div style={{ paddingBottom: "24px" }}>
          <div>
            <h3
              style={{
                fontSize: "22px",
                fontWeight: "700",
                color: "#1a1a1a",
                marginBottom: "8px",
                textAlign: "center",
              }}
            >
              {t("ChooseYourBarber")}
            </h3>
            <p
              style={{
                fontSize: "14px",
                color: "#6b7280",
                textAlign: "center",
                marginBottom: "20px",
              }}
            >
              {t("SelectBarberForAppointment")}
            </p>

            <div
              style={{ display: "flex", flexDirection: "column", gap: "12px" }}
            >
              {OtherBarbers.map((barber, index) => {
                const isSelected =
                  SelectedBarber?.phoneNumber === barber.phoneNumber;
                return (
                  <div
                    key={index}
                    onClick={() => setSelectedBarber(barber)}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "18px",
                      borderRadius: "14px",
                      backgroundColor: isSelected ? "#667eea" : "#fff",
                      color: isSelected ? "#fff" : "#1a1a1a",
                      border:
                        "2px solid " + (isSelected ? "#667eea" : "#e5e7eb"),
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      boxShadow: isSelected
                        ? "0 4px 16px rgba(102, 126, 234, 0.4)"
                        : "0 2px 6px rgba(0,0,0,0.06)",
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.borderColor = "#667eea";
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.boxShadow =
                          "0 4px 12px rgba(102, 126, 234, 0.2)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.borderColor = "#e5e7eb";
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow =
                          "0 2px 6px rgba(0,0,0,0.06)";
                      }
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "14px",
                      }}
                    >
                      {/* Barber Avatar */}
                      <div
                        style={{
                          width: "50px",
                          height: "50px",
                          borderRadius: "50%",
                          backgroundColor: isSelected ? "#fff" : "#667eea",
                          color: isSelected ? "#667eea" : "#fff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: "700",
                          fontSize: "18px",
                        }}
                      >
                        {barber.name?.charAt(0) || "B"}
                      </div>
                      <div>
                        <div
                          style={{
                            fontWeight: "600",
                            fontSize: "16px",
                            marginBottom: "4px",
                          }}
                        >
                          {barber.name || t("Barber")}
                        </div>
                        <div
                          style={{
                            fontSize: "13px",
                            opacity: 0.85,
                          }}
                        >
                          {barber.specialty || t("ProfessionalBarber")}
                        </div>
                      </div>
                    </div>
                    <div
                      style={{
                        width: "26px",
                        height: "26px",
                        borderRadius: "50%",
                        border: isSelected
                          ? "2px solid #fff"
                          : "2px solid #d1d5db",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: isSelected ? "#fff" : "transparent",
                      }}
                    >
                      {isSelected && (
                        <div
                          style={{
                            width: "14px",
                            height: "14px",
                            borderRadius: "50%",
                            backgroundColor: "#667eea",
                          }}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Empty State */}
          {/* {!SelectedBarber && (
            <div
              style={{
                marginTop: "24px",
                padding: "32px 20px",
                textAlign: "center",
                backgroundColor: "#fef9e7",
                borderRadius: "16px",
                border: "2px solid #fde68a",
              }}
            >
              <div style={{ fontSize: "48px", marginBottom: "12px" }}>💈</div>
              <div
                style={{
                  fontWeight: "600",
                  color: "#92400e",
                  marginBottom: "6px",
                  fontSize: "16px",
                }}
              >
                {t("SelectABarber")}
              </div>
              <div style={{ fontSize: "14px", color: "#b45309" }}>
                {t("ChoosePreferredBarber")}
              </div>
            </div>
          )} */}

          {/* Next Button */}
          <div
            style={{
              marginTop: "24px",
              paddingTop: "20px",
              borderTop: "2px solid #f3f4f6",
            }}
          >
            <button
              onClick={handleNextToServices}
              disabled={!SelectedBarber}
              style={{
                width: "100%",
                padding: "16px",
                fontSize: "16px",
                fontWeight: "600",
                border: "none",
                borderRadius: "12px",
                backgroundColor: !SelectedBarber ? "#e5e7eb" : "#667eea",
                color: !SelectedBarber ? "#9ca3af" : "#fff",
                cursor: !SelectedBarber ? "not-allowed" : "pointer",
                boxShadow: !SelectedBarber
                  ? "none"
                  : "0 4px 16px rgba(102, 126, 234, 0.4)",
                transition: "all 0.3s ease",
                outline: "none",
              }}
              onMouseEnter={(e) => {
                if (SelectedBarber) {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow =
                    "0 6px 20px rgba(102, 126, 234, 0.5)";
                }
              }}
              onMouseLeave={(e) => {
                if (SelectedBarber) {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 4px 16px rgba(102, 126, 234, 0.4)";
                }
              }}
            >
              {t("NextSelectServices")}
            </button>
          </div>
        </div>
      )}

      {/* STEP 5: Select Services */}
      {step === 5 && (
        <div style={{ paddingBottom: "24px" }}>
          {/* Selected Barber Banner */}
          {SelectedBarber && (
            <div
              style={{
                marginBottom: "20px",
                padding: "12px 16px",
                background: "linear-gradient(135deg, #f59e0b 0%, #f97316 100%)",
                borderRadius: "12px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                color: "#fff",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "50%",
                    backgroundColor: "#fff",
                    color: "#f59e0b",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: "700",
                    fontSize: "16px",
                  }}
                >
                  {SelectedBarber.name?.charAt(0) || "B"}
                </div>
                <div>
                  <div style={{ fontSize: "14px", fontWeight: "700" }}>
                    {SelectedBarber.name || t("Barber")}
                  </div>
                  <div style={{ fontSize: "12px", opacity: 0.9 }}>
                    {t("YourBarber")}
                  </div>
                </div>
              </div>
              {shouldShowBarberStep && (
                <button
                  onClick={handleBackToBarber}
                  style={{
                    fontSize: "12px",
                    fontWeight: "600",
                    color: "#fff",
                    backgroundColor: "rgba(255,255,255,0.2)",
                    border: "1px solid rgba(255,255,255,0.3)",
                    cursor: "pointer",
                    padding: "6px 12px",
                    borderRadius: "6px",
                    outline: "none",
                  }}
                >
                  {t("Change")}
                </button>
              )}
            </div>
          )}

          <div>
            <h3
              style={{
                fontSize: "22px",
                fontWeight: "700",
                color: "#1a1a1a",
                marginBottom: "8px",
                textAlign: "center",
              }}
            >
              {t("SelectYourServices")}
            </h3>
            <p
              style={{
                fontSize: "14px",
                color: "#6b7280",
                textAlign: "center",
                marginBottom: "20px",
              }}
            >
              {t("ChooseOneOrMoreServices")}
            </p>

            <div
              style={{ display: "flex", flexDirection: "column", gap: "12px" }}
            >
              {(() => {
                // Get the services for the selected barber
                let servicesToShow = [];

                if (SelectedBarber) {
                  // If barber is selected, find their services from allBarbersWithData
                  const barberData = AllData.allBarbersWithData?.find(
                    (b) => b.barber.phoneNumber === SelectedBarber.phoneNumber,
                  );
                  servicesToShow = barberData?.services || [];
                } else {
                  // If no barber selected (single barber scenario), show main barber services
                  servicesToShow = AllData.barberServices || [];
                }

                return servicesToShow.length > 0 ? (
                  servicesToShow.map((item, index) => {
                    const isSelected = SelectedServices.some(
                      (s) => s.Service === item.service,
                    );
                    return (
                      <div
                        key={index}
                        onClick={() => toggleService(item)}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "18px",
                          borderRadius: "14px",
                          backgroundColor: isSelected ? "#667eea" : "#fff",
                          color: isSelected ? "#fff" : "#1a1a1a",
                          border:
                            "2px solid " + (isSelected ? "#667eea" : "#e5e7eb"),
                          cursor: "pointer",
                          transition: "all 0.3s ease",
                          boxShadow: isSelected
                            ? "0 4px 16px rgba(102, 126, 234, 0.4)"
                            : "0 2px 6px rgba(0,0,0,0.06)",
                        }}
                      >
                        <div>
                          <div
                            style={{
                              fontWeight: "600",
                              fontSize: "16px",
                              marginBottom: "4px",
                            }}
                          >
                            {item.service}
                          </div>
                          <div
                            style={{
                              fontSize: "13px",
                              opacity: 0.85,
                            }}
                          >
                            {item.duration || "30"} {t("Minutes")}
                          </div>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "14px",
                          }}
                        >
                          <div style={{ fontWeight: "700", fontSize: "19px" }}>
                            {item.price} {currencySymbol}
                          </div>
                          <div
                            style={{
                              width: "26px",
                              height: "26px",
                              borderRadius: "50%",
                              border: isSelected
                                ? "2px solid #fff"
                                : "2px solid #d1d5db",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: isSelected
                                ? "#fff"
                                : "transparent",
                            }}
                          >
                            {isSelected && (
                              <div
                                style={{
                                  width: "14px",
                                  height: "14px",
                                  borderRadius: "50%",
                                  backgroundColor: "#667eea",
                                }}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div
                    style={{
                      padding: "40px 20px",
                      textAlign: "center",
                      backgroundColor: "#fef2f2",
                      borderRadius: "16px",
                      border: "2px solid #fca5a5",
                    }}
                  >
                    <div style={{ fontSize: "48px", marginBottom: "12px" }}>
                      ⚠️
                    </div>
                    <div
                      style={{
                        fontWeight: "600",
                        color: "#991b1b",
                        fontSize: "16px",
                      }}
                    >
                      {t("NoServicesAvailable")}
                    </div>
                    <div
                      style={{
                        fontSize: "14px",
                        color: "#dc2626",
                        marginTop: "6px",
                      }}
                    >
                      {t("ThisBarberHasNoServices")}
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Total Price & Duration */}
            {SelectedServices.length > 0 && (
              <div
                style={{
                  marginTop: "20px",
                  padding: "18px",
                  borderRadius: "14px",
                  backgroundColor: "#f9fafb",
                  border: "1px solid #e5e7eb",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "12px",
                  }}
                >
                  <span
                    style={{
                      fontWeight: "600",
                      fontSize: "16px",
                      color: "#374151",
                    }}
                  >
                    {t("TotalDuration")}
                  </span>
                  <span
                    style={{
                      fontWeight: "700",
                      fontSize: "19px",
                      color: "#374151",
                    }}
                  >
                    {totalDuration} {t("Minutes")}
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      fontWeight: "600",
                      fontSize: "16px",
                      color: "#374151",
                    }}
                  >
                    {t("TotalPrice")}
                  </span>
                  <span
                    style={{
                      fontWeight: "700",
                      fontSize: "22px",
                      color: "#667eea",
                    }}
                  >
                    {totalPrice.toFixed(2)} {currencySymbol}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Next Button */}
          <div
            style={{
              marginTop: "24px",
              paddingTop: "20px",
              borderTop: "2px solid #f3f4f6",
            }}
          >
            <button
              onClick={handleNextToDateTime}
              disabled={SelectedServices.length === 0}
              style={{
                width: "100%",
                padding: "16px",
                fontSize: "16px",
                fontWeight: "600",
                border: "none",
                borderRadius: "12px",
                backgroundColor:
                  SelectedServices.length === 0 ? "#e5e7eb" : "#667eea",
                color: SelectedServices.length === 0 ? "#9ca3af" : "#fff",
                cursor:
                  SelectedServices.length === 0 ? "not-allowed" : "pointer",
                boxShadow:
                  SelectedServices.length === 0
                    ? "none"
                    : "0 4px 16px rgba(102, 126, 234, 0.4)",
                transition: "all 0.3s ease",
                outline: "none",
              }}
              onMouseEnter={(e) => {
                if (SelectedServices.length > 0) {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow =
                    "0 6px 20px rgba(102, 126, 234, 0.5)";
                }
              }}
              onMouseLeave={(e) => {
                if (SelectedServices.length > 0) {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 4px 16px rgba(102, 126, 234, 0.4)";
                }
              }}
            >
              {t("NextSelectDateTime")}
            </button>
          </div>
        </div>
      )}

      {/* STEP 6: Select Date & Time */}
      {step === 6 && (
        <div style={{ paddingBottom: "24px" }}>
          {/* Selected Barber Banner */}
          {SelectedBarber && (
            <div
              style={{
                marginBottom: "16px",
                padding: "12px 16px",
                background: "linear-gradient(135deg, #f59e0b 0%, #f97316 100%)",
                borderRadius: "12px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                color: "#fff",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "50%",
                    backgroundColor: "#fff",
                    color: "#f59e0b",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: "700",
                    fontSize: "16px",
                  }}
                >
                  {SelectedBarber.name?.charAt(0) || "B"}
                </div>
                <div>
                  <div style={{ fontSize: "14px", fontWeight: "700" }}>
                    {SelectedBarber.name || t("Barber")}
                  </div>
                  <div style={{ fontSize: "12px", opacity: 0.9 }}>
                    {t("YourBarber")}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Select Date */}
          <div>
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "700",
                color: "#1a1a1a",
                marginBottom: "16px",
              }}
            >
              {t("SelectDate")}
            </h3>
            <div
              style={{
                display: "flex",
                overflowX: "auto",
                gap: "12px",
                padding: "4px 0",
                scrollbarWidth: "thin",
                scrollbarColor: "#cbd5e1 #f1f5f9",
              }}
            >
              {days.map((item, index) => {
                const isSelected = item.date === ChoosedDate;
                const isToday = item.date === moment().format("DD-MM-YYYY");
                return (
                  <div
                    key={index}
                    onClick={() => {
                      setChoosedDate(item.date);

                      // Get appointments for the selected barber
                      let todaysAppointments = [];

                      if (SelectedBarber) {
                        // Find the barber's data from allBarbersWithData
                        const barberData = AllData.allBarbersWithData?.find(
                          (b) =>
                            b.barber.phoneNumber === SelectedBarber.phoneNumber,
                        );

                        // Filter by date and isTaken === 0
                        todaysAppointments =
                          barberData?.appointments.filter(
                            (app) =>
                              app.date === item.date && app.isTaken === 0,
                          ) || [];
                      } else {
                        // If no barber selected, use the main barber's appointments
                        todaysAppointments = AllApps.filter(
                          (app) => app.date === item.date,
                        );
                      }

                      setChoosedApps(todaysAppointments);
                    }}
                    style={{
                      minWidth: "90px",
                      padding: "16px 12px",
                      borderRadius: "16px",
                      textAlign: "center",
                      cursor: "pointer",
                      backgroundColor: isSelected ? "#667eea" : "#fff",
                      color: isSelected ? "#fff" : "#1a1a1a",
                      border: isSelected
                        ? "2px solid #667eea"
                        : isToday
                          ? "2px solid #a78bfa"
                          : "2px solid #e5e7eb",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      boxShadow: isSelected
                        ? "0 8px 24px rgba(102, 126, 234, 0.35)"
                        : "0 2px 8px rgba(0,0,0,0.04)",
                      flexShrink: 0,
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.transform =
                          "translateY(-4px) scale(1.02)";
                        e.currentTarget.style.boxShadow =
                          "0 6px 20px rgba(102, 126, 234, 0.2)";
                        e.currentTarget.style.borderColor = "#a78bfa";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.transform =
                          "translateY(0) scale(1)";
                        e.currentTarget.style.boxShadow =
                          "0 2px 8px rgba(0,0,0,0.04)";
                        e.currentTarget.style.borderColor = isToday
                          ? "#a78bfa"
                          : "#e5e7eb";
                      }
                    }}
                  >
                    <div
                      style={{
                        fontSize: "10px",
                        fontWeight: "600",
                        marginBottom: "4px",
                        opacity: isSelected ? 0.9 : 0.6,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      {item.monthName}
                    </div>

                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: "700",
                        marginBottom: "8px",
                        opacity: isSelected ? 1 : 0.75,
                        textTransform: "capitalize",
                      }}
                    >
                      {item.day}
                    </div>

                    <div
                      style={{
                        fontSize: "18px",
                        fontWeight: "600",
                        lineHeight: "1",
                        marginBottom: "6px",
                      }}
                    >
                      {item.dayNum}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Available Times */}
          <div style={{ marginTop: "28px" }}>
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "700",
                color: "#1a1a1a",
                marginBottom: "16px",
              }}
            >
              {t("AvailableTimes")}
            </h3>

            {(() => {
              const validIndexes = getAvailableStartingSlots(
                ChoosedApps,
                totalDuration,
              );

              return validIndexes.length === 0 ? (
                <div
                  style={{
                    padding: "40px 20px",
                    textAlign: "center",
                    backgroundColor: "#fef2f2",
                    borderRadius: "16px",
                    border: "2px solid #fca5a5",
                  }}
                >
                  <div style={{ fontSize: "48px", marginBottom: "12px" }}>
                    📅
                  </div>
                  <div
                    style={{
                      fontWeight: "600",
                      color: "#991b1b",
                      fontSize: "16px",
                    }}
                  >
                    {t("NoAvailableAppointments")}
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      color: "#dc2626",
                      marginTop: "6px",
                    }}
                  >
                    {totalDuration > 0
                      ? t("NoConsecutiveSlotsFound", {
                          duration: totalDuration,
                        })
                      : t("PleaseSelectAnotherDate")}
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    flexWrap: "wrap",
                    height: "400px",
                    gap: "12px",
                    overflowX: "auto",
                    overflowY: "hidden",
                    paddingBottom: "10px",
                  }}
                >
                  {validIndexes.map((index) => {
                    const item = ChoosedApps[index];
                    return (
                      <div
                        key={index}
                        onClick={() => {
                          // Add selected barber info to the appointment data
                          const appointmentData = {
                            ...item,
                            selectedBarber: SelectedBarber, // Pass the selected barber
                          };
                          setObjectData(appointmentData);
                          setOpenDrawer(!OpenDrawer);
                        }}
                        style={{
                          width: "100px",
                          height: "calc(25% - 9px)",
                          padding: "18px 14px",
                          borderRadius: "14px",
                          textAlign: "center",
                          backgroundColor: "#fff",
                          border: "1px solid #e5e7eb",
                          cursor: "pointer",
                          transition: "all 0.3s ease",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
                          flexShrink: 0,
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#667eea";
                          e.currentTarget.style.color = "#fff";
                          e.currentTarget.style.transform = "scale(1.05)";
                          e.currentTarget.style.boxShadow =
                            "0 6px 20px rgba(102, 126, 234, 0.4)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "#fff";
                          e.currentTarget.style.color = "#1a1a1a";
                          e.currentTarget.style.transform = "scale(1)";
                          e.currentTarget.style.boxShadow =
                            "0 2px 6px rgba(0,0,0,0.06)";
                        }}
                      >
                        <div style={{ fontSize: "17px", fontWeight: "700" }}>
                          {item.timeStart}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>

          {/* Back Button */}
          <div
            style={{
              marginTop: "24px",
              paddingTop: "20px",
              borderTop: "2px solid #f3f4f6",
            }}
          >
            <button
              onClick={handleBackToServices}
              style={{
                width: "100%",
                padding: "16px",
                fontSize: "16px",
                fontWeight: "600",
                border: "1px solid #e5e7eb",
                borderRadius: "12px",
                backgroundColor: "#fff",
                color: "#6b7280",
                cursor: "pointer",
                transition: "all 0.3s ease",
                outline: "none",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#f9fafb";
                e.currentTarget.style.borderColor = "#667eea";
                e.currentTarget.style.color = "#667eea";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#fff";
                e.currentTarget.style.borderColor = "#e5e7eb";
                e.currentTarget.style.color = "#6b7280";
              }}
            >
              {t("BackToServices")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
